import React, { useContext, useEffect, useState } from "react";
import DashboardLayout from "../../components/Layout/DashboardLayout";
import Card from "../../components/UI/Card";
import Input from "../../components/UI/Input";
import Button from "../../components/UI/Button";
import Badge from "../../components/UI/Badge";
import LoadingSpinner from "../../components/UI/LoadingSpinner";
import { AuthContext } from "../../context/AuthContext";
import * as authService from "../../services/authService";
import * as creditService from "../../services/creditService";
import { toast } from "react-toastify";

export default function Profile() {
  const { user, updateUser } = useContext(AuthContext);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [referral, setReferral] = useState({ code: "", total: 0, credits: 0 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const prof = await authService.getProfile();
        if (mounted) {
          setForm({
            name: prof.user?.name || "",
            email: prof.user?.email || "",
            phone: prof.user?.phone || "",
          });
        }
        const stats = await creditService.getReferralStats();
        if (mounted) {
          setReferral({
            code: stats.referralCode || "",
            total: stats.totalReferrals || 0,
            credits: stats.totalReferralCredits || 0,
          });
        }
      } catch {
        // ignore
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  function onChange(e) {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await authService.updateProfile({
        name: form.name,
        phone: form.phone,
        email: form.email,
      });
      await updateUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setSaving(false);
    }
  }

  function copy(text) {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard");
  }

  if (loading) {
    return (
      <DashboardLayout>
        <LoadingSpinner fullScreen />
      </DashboardLayout>
    );
  }

  const shareLink = `${window.location.origin}/register?ref=${referral.code}`;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <h1 className="text-2xl font-bold">Your Profile</h1>

        <Card title="Personal Information">
          <form
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
            onSubmit={onSubmit}
          >
            <Input
              name="name"
              label="Name"
              value={form.name}
              onChange={onChange}
              required
            />
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <div className="flex items-center gap-2">
                <input
                  value={form.email}
                  disabled
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 bg-gray-50"
                />
                <Badge
                  text={user?.isEmailVerified ? "Verified" : "Unverified"}
                  variant={user?.isEmailVerified ? "success" : "warning"}
                />
              </div>
            </div>
            <Input
              name="phone"
              label="Phone"
              value={form.phone}
              onChange={onChange}
            />
            <div className="md:col-span-2">
              <Button type="submit" loading={saving}>
                Update Profile
              </Button>
            </div>
          </form>
        </Card>

        <Card title="Account Settings">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <div>
              <h3 className="font-semibold mb-2">Change Password</h3>
              <div className="grid grid-cols-1 gap-3">
                <Input name="cur" type="password" label="Current Password" />
                <Input name="npw" type="password" label="New Password" />
                <Input
                  name="cpw"
                  type="password"
                  label="Confirm New Password"
                />
                <Button
                  type="button"
                  onClick={() => toast.info("Password change coming soon")}
                >
                  Change Password
                </Button>
              </div>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Email Preferences</h3>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" defaultChecked /> Receive product updates
              </label>
              <label className="flex items-center gap-2 text-sm mt-2">
                <input type="checkbox" defaultChecked /> Receive tips and best
                practices
              </label>
              <div className="mt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => toast.info("Preferences saved")}
                >
                  Save Preferences
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-6">
            <h3 className="font-semibold mb-2 text-red-600">Danger Zone</h3>
            <Button
              type="button"
              variant="danger"
              onClick={() =>
                toast.error("Account deletion not implemented yet")
              }
            >
              Delete Account
            </Button>
          </div>
        </Card>

        <Card title="Referral Information">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
            <div>
              <p className="text-xs sm:text-sm text-gray-500">
                Your referral code
              </p>
              <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center gap-2">
                <span className="font-mono text-xs sm:text-sm bg-gray-50 px-2 py-1 rounded border break-all">
                  {referral.code || "-"}
                </span>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => copy(referral.code)}
                  disabled={!referral.code}
                  className="whitespace-nowrap"
                >
                  Copy
                </Button>
              </div>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500">
                Total referrals
              </p>
              <p className="mt-1 text-lg sm:text-xl font-bold">
                {referral.total}
              </p>
            </div>
            <div>
              <p className="text-xs sm:text-sm text-gray-500">Credits earned</p>
              <p className="mt-1 text-lg sm:text-xl font-bold">
                {referral.credits}
              </p>
            </div>
          </div>
          <div className="mt-4">
            <p className="text-xs sm:text-sm text-gray-500">Shareable link</p>
            <div className="mt-1 flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <input
                value={shareLink}
                readOnly
                className="flex-1 min-w-0 rounded-md border border-gray-300 px-3 py-2 bg-gray-50 text-xs sm:text-sm"
              />
              <Button
                size="sm"
                variant="ghost"
                onClick={() => copy(shareLink)}
                disabled={!referral.code}
                className="whitespace-nowrap"
              >
                Copy Link
              </Button>
            </div>
          </div>
        </Card>
      </div>
    </DashboardLayout>
  );
}
